/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './AccountDetailsHeader.scss';

import { number, object, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import AccountSelection from 'components/layouts/AccountDetails/AccountSelection';
import BaseModal from 'components/shared/Modal/BaseModal';
import AccountRegistration from './AccountRegistration';
import { fundBookUrlByTicker } from 'services/Externals';
import { MUTUAL_FUND, isBrokerage } from 'services/Products';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import AccountLabel from 'components/shared/AccountLabel/AccountLabel';
import BrokerageAccountSelection from './BrokerageAccountSelection';
import {
  Owners,
  AccountNumber
} from 'components/shared/GroupedAccountList/util';
import infoHelpIcon from 'assets/icons/info-help-icon-filled.svg';
import ToolTip from 'components/shared/ToolTip/ToolTip';
import { AAMBeaconModal } from 'components/shared/Beacon';
import {Dollar} from "../../shared/Text/Text";

const AccountNumberWithPermissions = WithUserPermissions(AccountNumber);
const OwnersWithPermissions = WithUserPermissions(Owners);
const AccountRegistrationWithPermissions =
  WithUserPermissions(AccountRegistration);

const AccountDetailsHeader = ({
  account,
  numberOfAccounts,
  owners,
  history,
  positions,
  currentCusip,
  sweepAccountsMap
}) => {
  const isMFESweepAccount =
    account?.sweepAccount && account?.productType === MUTUAL_FUND;
  const [accountRegistrationModalOpen, setAccountRegistrationModalOpen] =
    useState(false);
  const [isBrokerageAMA, setIsBrokerageAMA] = useState(false);
  const [brokeragePosition, setBrokeragePosition] = useState('');

  useEffect(() => {
    if (currentCusip) {
      const currentAcc = positions.find((acc) => acc.cusip === currentCusip);
      setIsBrokerageAMA(
        currentAcc?.trpCode === 'USTO' || currentAcc?.trpCode === 'PRFO'
      );
      setBrokeragePosition(currentAcc);
    }
  }, []);

  const handleModalClose = () => {
    const modal = document.getElementById('account-selection-modal') ?? null;
    if (modal !== null) {
      modal.close();
    }
  };

  const handleModalOpen = () => {
    const modal = document.getElementById('account-selection-modal');
    if (modal !== null) {
      modal.show();
    }
  };

  const handleAccountRegModalClose = () =>
    setAccountRegistrationModalOpen(false);

  const handleAccountRegModalOpen = () => setAccountRegistrationModalOpen(true);

  const handleOnSelect = (accountId) => {
    handleModalClose();
    history.push(`/account-details?id=${accountId}`);
  };

  const handleOnBrokerageSelect = (cusip, selAccount) => {
    handleModalClose();
    const { accountId } = account;
    setBrokeragePosition(selAccount);
    history.push(`/account-details?id=${accountId}&investment=${cusip}`);
  };

  const mtsAccountBalance =
    /*isBrokerageAMA*/isMFESweepAccount
      ? sweepAccountsMap[account.accountId]?.balanceDetails?.filter(
          (accountDetail) =>
            accountDetail?.balanceTypeValue.balanceTypeName === 'Sweep_Accrual'
        )?.[0]?.amount || 0
      : isBrokerageAMA ?
        account?.balanceDetails?.filter(
          (accountDetail) =>
            accountDetail?.balanceTypeValue.balanceTypeName === 'Sweep_Accrual'
        )?.[0]?.amount || 0
      : null;

  return (
    <div className="account-details-header account-details-header">
      <AAMBeaconModal scale="jumbo" id="account-selection-modal">
        {positions &&
        isBrokerage(account.productType) ? (
          <BrokerageAccountSelection
            account={account}
            currentCusip={currentCusip}
            onSelect={handleOnBrokerageSelect}
          />
        ) : (
          <AccountSelection
            currentAccountId={account?.accountId}
            onSelect={handleOnSelect}
          />
        )}
      </AAMBeaconModal>
      <BaseModal
        close={handleAccountRegModalClose}
        className="account-registration-background-modal"
        hideAnchor={true}
        isOpen={accountRegistrationModalOpen}
        label="account-registration-modal"
        title={null}>
        <AccountRegistrationWithPermissions
          accountRegistration={account?.accountRegistration}
          accountNumber={account?.accountNumber}
          accountChecksum={account?.accountChecksum}
          registrationAddress={account?.registrationAddress}
          shouldMask
        />
      </BaseModal>
      <div>
        <span className="header-type">{account?.accountType}</span>
      </div>
      <div className="account-details-header-flex-container">
        <div className="header-flex">
          <div className="header-name-and-number">
            <h1 className="brokerage-header-name">
              {positions ? brokeragePosition?.name : account?.accountName}
              <span className="header-ticker-symbol">
              {' '}
                {positions ? brokeragePosition?.ticker : account?.ticker}
            </span>
            </h1>
            <AccountLabel accountId={account?.accountId} />
            <div className="brokerage-sub-header">
              <AccountNumberWithPermissions
                accountNumber={account?.accountNumber}
                accountChecksum={account?.accountChecksum}
                shouldMask
              />
              <span className="spacing-dot" />
              <OwnersWithPermissions
                owners={owners}
                shouldMask
              />
              {(isBrokerageAMA || isMFESweepAccount) && (
                <div className="mtd-accrual-account-details-info">
                  <span className="spacing-dot-account-details" />
                  <span className="mtd-no-break">MTD Accrual: <Dollar>{mtsAccountBalance || 0}</Dollar></span>
                  <ToolTip
                    className="cost-basis-tooltip-v2"
                    tooltipLabel={
                      <div className="cost-basis-tooltip-v2-box">
                        <div className="cost-basis-tooltip-v2-header">
                          MTD Accrual
                        </div>
                        <div className="cost-basis-tooltip-v2-content">
                          The total dollar value including the interest accrued
                          from the month beginning until today’s date
                        </div>
                      </div>
                    }>
                    <img
                      src={infoHelpIcon}
                      alt="info"
                      className="account-details-tooltip-v2-image"
                    />
                  </ToolTip>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="change-investments-container">
          {(numberOfAccounts > 1 ||
            (positions &&
              isBrokerage(account.productType) &&
              positions.length > 1)) && (
            <button
              className="brokerage-change-inv change-investments"
              onClick={handleModalOpen}>
              {isBrokerage(account.productType)
                ? 'Change Holdings'
                : 'Change Accounts'}
            </button>
          )}
        </div>
      </div>
      <div className="more-information-container">
        <div className="registration-details">
          <button
            className="view-regisitration-button"
            onClick={handleAccountRegModalOpen}>
            View Registration Details
          </button>
        </div>
      </div>
      {!(
        isBrokerage(account.productType)
      ) && (
        <div className="more-information-container">
          <span>
            <a
              id="account-more-information-link"
              className="fund-info"
              href={fundBookUrlByTicker(account?.ticker)}
              target="_blank"
              rel="noreferrer">
              View Mutual Fund Information
            </a>
          </span>
        </div>
      )}
    </div>
  );
};

AccountDetailsHeader.propTypes = {
  account: object.isRequired,
  history: object.isRequired,
  numberOfAccounts: number.isRequired,
  owners: string.isRequired,
  positions: object,
  currentCusip: number
};

export default withRouter(AccountDetailsHeader);
